import { getDoc } from 'https://www.gstatic.com/firebasejs/9.17.2/firebase-firestore.js';
import { docWithTenant } from '../utils/firestoreHelpers.js';
import { normalizeContactTimeZone } from '../utils/contactTimeZone.js';
import { resolveActiveTenantId } from '../utils/tenantUtils.js';

const CONTACT_TIME_ZONE_CACHE = new Map();

/**
 * Resolve the contact identifier carried by a Focus Mode task payload.
 *
 * @param {Record<string, unknown>|null|undefined} task - Focus Mode task payload.
 * @returns {string|null} Contact identifier or `null`.
 */
function resolveTaskContactId(task) {
  if (!task || typeof task !== 'object') return null;
  const candidates = [
    task.contactId,
    task.associated_contact_id,
    task.contact_id,
    task.contact?.id,
  ];
  for (const candidate of candidates) {
    const value = String(candidate || '').trim();
    if (value) return value;
  }
  return null;
}

/**
 * Clear the cached contact-timezone lookups.
 *
 * @returns {void}
 */
export function clearContactTimeZoneResolverCache() {
  CONTACT_TIME_ZONE_CACHE.clear();
}

/**
 * Resolve a contact timezone from tenant-owned Firestore data only.
 *
 * Lookup order is:
 *  1. Task payload fields already present in memory
 *  2. `/ContactMeta/{contactId}`
 *  3. `/ContactsMeta/{contactId}`
 *  4. `/ContactsStaging/{contactId}` or `findByCrmId(contactId)`
 *
 * @param {{
 *   task?: Record<string, unknown>|null,
 *   contactId?: string|null,
 *   tenantId?: string|null,
 *   services?: {
 *     auth?: { currentUser?: { tenantId?: string|null } },
 *     contactsStagingService?: { findByCrmId?: (contactId: string) => Promise<{ id: string, data: Record<string, unknown> }|null> },
 *   },
 * }} [params={}] - Lookup inputs and service overrides.
 * @returns {Promise<string|null>} Valid IANA timezone or `null`.
 */
export async function resolveContactTimeZone({
  task = null,
  contactId = null,
  tenantId = null,
  services = {},
} = {}) {
  const inMemory = normalizeContactTimeZone(
    task?.contactTimeZone ||
      task?.contact?.contactTimeZone ||
      task?.contact?.timeZone ||
      task?.metadata?.contactTimeZone
  );
  if (inMemory) {
    return inMemory;
  }

  const resolvedContactId =
    typeof contactId === 'string' && contactId.trim()
      ? contactId.trim()
      : resolveTaskContactId(task);
  let activeTenantId = null;
  try {
    activeTenantId = resolveActiveTenantId(services) || null;
  } catch {
    activeTenantId = null;
  }
  const resolvedTenantId =
    typeof tenantId === 'string' && tenantId.trim()
      ? tenantId.trim()
      : activeTenantId || services?.auth?.currentUser?.tenantId || null;
  if (!resolvedContactId || !resolvedTenantId) {
    return null;
  }

  const cacheKey = `${resolvedTenantId}:${resolvedContactId}`;
  if (CONTACT_TIME_ZONE_CACHE.has(cacheKey)) {
    return CONTACT_TIME_ZONE_CACHE.get(cacheKey) ?? null;
  }

  let resolved = null;
  const refs = [
    `ContactMeta/${resolvedContactId}`,
    `ContactsMeta/${resolvedContactId}`,
    `ContactsStaging/${resolvedContactId}`,
  ];

  for (const path of refs) {
    try {
      const snap = await getDoc(docWithTenant(resolvedTenantId, path));
      if (!snap?.exists?.()) continue;
      const data = typeof snap.data === 'function' ? snap.data() : {};
      resolved = normalizeContactTimeZone(data?.contactTimeZone);
      if (resolved) break;
    } catch (err) {
      console.debug('[contactTimeZoneResolver] local lookup skipped', {
        path,
        err,
      });
    }
  }

  if (!resolved && services?.contactsStagingService?.findByCrmId) {
    try {
      const staged =
        await services.contactsStagingService.findByCrmId(resolvedContactId);
      resolved = normalizeContactTimeZone(staged?.data?.contactTimeZone);
    } catch (err) {
      console.debug(
        '[contactTimeZoneResolver] staged lookup by CRM id skipped',
        err
      );
    }
  }

  CONTACT_TIME_ZONE_CACHE.set(cacheKey, resolved || null);
  return resolved || null;
}
